import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, RefreshCw, Calendar, MapPin, Briefcase } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SearchCriteria } from '@/pages/People';

interface SearchHistoryItem {
  id: string;
  search_criteria: SearchCriteria;
  result_count: number;
  created_at: string;
  exported: boolean;
}

interface SearchHistoryProps {
  onRerunSearch: (criteria: SearchCriteria) => void;
}

export const SearchHistory = ({ onRerunSearch }: SearchHistoryProps) => {
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('prospect_searches')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setHistory((data as unknown as SearchHistoryItem[]) || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load search history');
    } finally {
      setLoading(false);
    }
  };

  const deleteSearch = async (id: string) => {
    try {
      const { error } = await supabase
        .from('prospect_searches')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setHistory(history.filter(item => item.id !== id));
      toast.success('Search deleted successfully');
    } catch (error) {
      console.error('Error deleting search:', error);
      toast.error('Failed to delete search');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Loading search history...</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="p-12 text-center">
        <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No search history yet</h3>
        <p className="text-muted-foreground">Your previous searches will appear here</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {history.map((item) => (
        <Card key={item.id} className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <span className="text-gray-900 font-medium">{item.search_criteria.jobTitle}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-gray-700">{item.search_criteria.location}</span>
                </div>
                {item.search_criteria.locationType === 'radius' && (
                  <Badge variant="secondary" className="text-xs">
                    {item.search_criteria.radius} miles
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(item.created_at)}</span>
                </div>
                <span>•</span>
                <span>{item.result_count} results</span>
                {item.exported && (
                  <>
                    <span>•</span>
                    <Badge variant="outline" className="text-xs">Exported</Badge>
                  </>
                )}
              </div>

              {(item.search_criteria.companyName || item.search_criteria.industry) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {item.search_criteria.companyName && (
                    <Badge variant="secondary" className="text-xs">
                      Company: {item.search_criteria.companyName}
                    </Badge>
                  )}
                  {item.search_criteria.industry && (
                    <Badge variant="secondary" className="text-xs">
                      Industry: {item.search_criteria.industry}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRerunSearch(item.search_criteria)}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => deleteSearch(item.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
